/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { InvalidPromoCode } from '@ahamove/icons/order';
import type { CreateIntercityOrderRequestBody } from '@ahm/api-wrappers-core';
import { Box, Button, Stack, Text, Title, rem } from '@mantine/core';
import type { ContextModalProps } from '@mantine/modals';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { ModalName, showModal } from '../..';
import { useCreateOrder } from '@/features/booking/hooks/use-create-order';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type ModalCreateOrderWithoutPromoProps = {
  promo_id: string;
  createRequestBody: CreateIntercityOrderRequestBody & {
    promo_code_ids: string[];
  };
};

export default function CreateOrderWithoutPromo({
  innerProps,
  context,
}: ContextModalProps<ModalCreateOrderWithoutPromoProps>) {
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const tBooking = useTranslations('booking');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paramPromo, setParamPromo] = useQueryState(
    'promo_code',
    parseAsArrayOf(parseAsString).withDefault([])
  );
  const {
    paymentMethod: selectedPaymentMethod,
    estimatedData,
    setIsSuggestPromotion,
    errorPromo,
  } = useBookingStore((state) => state);

  const discountDetails = useMemo(
    () => estimatedData?.data?.discount_details ?? [],
    [estimatedData?.data]
  );

  const validPromoIds = useMemo(() => {
    if (errorPromo) return [];

    return (
      discountDetails
        .filter((item) => item.is_valid)
        .map((item) => item.promo_code_id) || []
    );
  }, [discountDetails, errorPromo]);

  const paramCreateOrder = useMemo(() => {
    return {
      ...innerProps.createRequestBody,
      promo_code_ids: validPromoIds,
    };
  }, [innerProps.createRequestBody, validPromoIds]);

  const handleCreateOrder = useCreateOrder(
    paramCreateOrder,
    pathname,
    selectedPaymentMethod,
    null,
    isMobile
  );

  const handleCreateOrderWithPromoValid = useCallback(async () => {
    if (!innerProps.createRequestBody) {
      return;
    }

    try {
      if (!validPromoIds.length) {
        await setParamPromo(null);
        setIsSuggestPromotion(false);
      } else {
        await setParamPromo(validPromoIds);
      }

      context.closeAll();

      await handleCreateOrder();
    } catch (error) {
      console.error('Error creating order with valid promo:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    innerProps.createRequestBody,
    validPromoIds,
    setParamPromo,
    handleCreateOrder,
  ]);

  const handleReviewPromoCode = useCallback(() => {
    context.closeAll();
    showModal(
      ModalName.Promotion,
      {
        isClearPromos: false,
      },
      {
        fullScreen: isMobile,
        centered: true,
        closeOnClickOutside: false,
        closeOnEscape: false,
        onClose: () => {
          //
        },
        padding: 0,
        styles: {
          root: {
            background: '#fff',
            padding: 0,
          },
          header: {
            background: '#fff',
            padding: '0px 24px',
          },
          content: {
            background: '#fff',
            padding: '0px',
          },
          body: {
            padding: '0px',
            background: '#fff',
          },
        },
      }
    );
  }, [context, isMobile]);

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
      }}
    >
      {!isMobile ? (
        <Box
          pt={24}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <InvalidPromoCode className="h-32 w-44" />
        </Box>
      ) : null}

      <Stack gap={10} p={24} pb={rem(8)} align="center">
        <Title order={4} fz={20} c={'neutral.7'}>
          {tBooking('promo.promo_code_error')}
        </Title>
        <Text c="neutral.6" fz={14} fw={500} ta="center">
          {tBooking('promo.promo_code_error_content')}
        </Text>
      </Stack>
      <Stack p={16}>
        <Button
          fullWidth
          size="md"
          h={rem(48)}
          onClick={handleCreateOrderWithPromoValid}
        >
          {tBooking('promo.button_booking_without_promo_code')}
        </Button>
        <Button
          fullWidth
          styles={{
            root: {
              backgroundColor: '#E6E9EC',
              ':hover': {
                backgroundColor: '#d5d6d6',
              },
            },
            inner: {
              color: '#072553',
            },
          }}
          variant="light"
          size="md"
          h={rem(48)}
          onClick={handleReviewPromoCode}
        >
          {tBooking('promo.button_checkout_this_promo_code')}
        </Button>
      </Stack>
    </Box>
  );
}
